import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { setGlobalAuthToken, setGlobalTenantCode } from '@/api/core/client'
import {
  DEV_MODE,
  devUser,
  inferRealmFromIssuer,
  keycloakConfig,
  REDIRECT_URI,
  resolveKeycloakConfig,
  resolveKeycloakConfigFromSearch,
} from '@/lib/auth/config'
import type { KeycloakConfig } from '@/lib/auth/config'
import { cleanupStalePKCEStates, generatePKCE, retrievePKCEState, storePKCEState } from '@/lib/auth/pkce'
import { parseOAuthParams, exchangeCodeForTokens, refreshAccessToken } from '@/lib/auth/oauth'
import { tokenHasScope, userFromToken } from '@/lib/auth/jwt'
import {
  clearRefreshToken,
  getRefreshSession,
  getRefreshToken,
  setRefreshToken,
} from '@/lib/auth/refresh-token-store'
import type { AuthContextType, User } from '@/lib/auth/types'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function resolveAuthenticatedState(
  accessToken: string,
  fallbackConfig: KeycloakConfig
): { authenticatedUser: User; authenticatedKeycloakConfig: KeycloakConfig } {
  const authenticatedUser = userFromToken(accessToken)
  const authenticatedKeycloakConfig = resolveKeycloakConfig({
    tenantCode: authenticatedUser.tenantCode || fallbackConfig.tenantCode,
    realm: inferRealmFromIssuer(authenticatedUser.issuer) || fallbackConfig.realm,
  })

  return {
    authenticatedUser,
    authenticatedKeycloakConfig,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_MODE)
  const [isLoading, setIsLoading] = useState(!DEV_MODE)
  const [user, setUser] = useState<User | null>(DEV_MODE ? devUser : null)
  const [token, setToken] = useState<string | null>(null)
  const [activeKeycloakConfig, setActiveKeycloakConfig] = useState(() =>
    typeof window === 'undefined' ? keycloakConfig : resolveKeycloakConfigFromSearch(window.location.search)
  )

  useEffect(() => {
    setGlobalAuthToken(token)
  }, [token])

  useEffect(() => {
    setGlobalTenantCode(user?.tenantCode || activeKeycloakConfig.tenantCode || null)
  }, [activeKeycloakConfig.tenantCode, user?.tenantCode])

  useEffect(() => {
    if (DEV_MODE) {
      setActiveKeycloakConfig(resolveKeycloakConfigFromSearch(window.location.search))
      setIsLoading(false)
      return
    }

    const handleAuth = async () => {
      try {
        cleanupStalePKCEStates()

        const oauthParams = parseOAuthParams()
        const isCallback = oauthParams.source !== 'none'

        console.log('[Auth] Initializing', {
          isCallback,
          hasCode: !!oauthParams.code,
          hasState: !!oauthParams.state,
          hasError: !!oauthParams.error,
          location: window.location.href,
        })

        if (oauthParams.error) {
          console.error('[Auth] OAuth error from Keycloak', {
            error: oauthParams.error,
            description: oauthParams.errorDescription,
          })
          window.history.replaceState(null, '', window.location.pathname)
          setIsLoading(false)
          return
        }

        if (isCallback && oauthParams.code && oauthParams.state) {
          console.log('[Auth] Processing OAuth callback')

          const pkceState = retrievePKCEState(oauthParams.state)
          if (!pkceState) {
            console.error('[Auth] PKCE state not found - cannot complete authentication')
            window.history.replaceState(null, '', window.location.pathname)
            setIsLoading(false)
            return
          }

          const callbackKeycloakConfig = resolveKeycloakConfig({
            tenantCode: pkceState.tenantCode,
            realm: pkceState.realm,
          })
          const tokens = await exchangeCodeForTokens(
            oauthParams.code,
            pkceState.codeVerifier,
            callbackKeycloakConfig
          )
          const { authenticatedUser, authenticatedKeycloakConfig } = resolveAuthenticatedState(
            tokens.accessToken,
            callbackKeycloakConfig
          )

          setToken(tokens.accessToken)
          setUser(authenticatedUser)
          setIsAuthenticated(true)
          setActiveKeycloakConfig(authenticatedKeycloakConfig)
          setRefreshToken(tokens.refreshToken || null, authenticatedKeycloakConfig)

          window.history.replaceState(null, '', window.location.pathname)

          console.log('[Auth] Authentication successful', {
            username: authenticatedUser.username,
            roles: authenticatedUser.roles,
            tenantCode: authenticatedUser.tenantCode,
            realm: authenticatedKeycloakConfig.realm,
          })
          return
        }

        const storedRefreshSession = getRefreshSession()
        if (storedRefreshSession) {
          console.log('[Auth] Restoring session from stored refresh token', {
            realm: storedRefreshSession.realm,
            tenantCode: storedRefreshSession.tenantCode,
          })

          const storedKeycloakConfig = resolveKeycloakConfig({
            tenantCode: storedRefreshSession.tenantCode,
            realm: storedRefreshSession.realm,
          })
          const tokens = await refreshAccessToken(storedRefreshSession.refreshToken, storedKeycloakConfig)
          const { authenticatedUser, authenticatedKeycloakConfig } = resolveAuthenticatedState(
            tokens.accessToken,
            storedKeycloakConfig
          )

          setToken(tokens.accessToken)
          setUser(authenticatedUser)
          setIsAuthenticated(true)
          setActiveKeycloakConfig(authenticatedKeycloakConfig)
          setRefreshToken(tokens.refreshToken || storedRefreshSession.refreshToken, authenticatedKeycloakConfig)

          console.log('[Auth] Stored session restored', {
            username: authenticatedUser.username,
            roles: authenticatedUser.roles,
            tenantCode: authenticatedUser.tenantCode,
            realm: authenticatedKeycloakConfig.realm,
          })
        }
      } catch (error) {
        console.error('[Auth] Authentication failed', {
          error,
          message: error instanceof Error ? error.message : String(error),
        })
        setIsAuthenticated(false)
        setUser(null)
        setToken(null)
        clearRefreshToken()
      } finally {
        setIsLoading(false)
      }
    }

    handleAuth()
  }, [])

  useEffect(() => {
    if (DEV_MODE || !isAuthenticated || !getRefreshToken()) {
      return
    }

    const refreshInterval = setInterval(async () => {
      const refreshToken = getRefreshToken()
      if (!refreshToken) {
        return
      }

      try {
        console.log('[Auth] Refreshing token')
        const tokens = await refreshAccessToken(refreshToken, activeKeycloakConfig)
        const { authenticatedUser, authenticatedKeycloakConfig } = resolveAuthenticatedState(
          tokens.accessToken,
          activeKeycloakConfig
        )

        setToken(tokens.accessToken)
        setUser(authenticatedUser)
        setActiveKeycloakConfig(authenticatedKeycloakConfig)
        setRefreshToken(tokens.refreshToken || refreshToken, authenticatedKeycloakConfig)
        console.log('[Auth] Token refreshed successfully')
      } catch (error) {
        console.error('[Auth] Token refresh failed', error)
        setIsAuthenticated(false)
        setUser(null)
        setToken(null)
        clearRefreshToken()
      }
    }, 60000)

    return () => clearInterval(refreshInterval)
  }, [activeKeycloakConfig, isAuthenticated])

  const login = useCallback(async () => {
    if (DEV_MODE && devUser) {
      setActiveKeycloakConfig(resolveKeycloakConfigFromSearch(window.location.search))
      setIsAuthenticated(true)
      setUser(devUser)
      return
    }

    try {
      const { codeVerifier, codeChallenge } = await generatePKCE()
      const state = crypto.randomUUID()
      const nonce = crypto.randomUUID()
      const nextKeycloakConfig = resolveKeycloakConfigFromSearch(window.location.search)

      const stored = storePKCEState(
        state,
        codeVerifier,
        nonce,
        nextKeycloakConfig.realm,
        nextKeycloakConfig.tenantCode
      )
      if (!stored) {
        console.error('[Auth] Failed to store PKCE state - cannot proceed with login')
        return
      }

      const authUrl = new URL(
        `${nextKeycloakConfig.url}/realms/${nextKeycloakConfig.realm}/protocol/openid-connect/auth`
      )
      authUrl.searchParams.set('client_id', nextKeycloakConfig.clientId)
      authUrl.searchParams.set('redirect_uri', REDIRECT_URI)
      authUrl.searchParams.set('response_type', 'code')
      authUrl.searchParams.set('response_mode', 'fragment')
      authUrl.searchParams.set('scope', 'openid')
      authUrl.searchParams.set('state', state)
      authUrl.searchParams.set('nonce', nonce)
      authUrl.searchParams.set('code_challenge', codeChallenge)
      authUrl.searchParams.set('code_challenge_method', 'S256')

      console.log('[Auth] Redirecting to Keycloak', {
        realm: nextKeycloakConfig.realm,
        tenantCode: nextKeycloakConfig.tenantCode,
        state: `${state.substring(0, 8)}...`,
        codeChallenge: `${codeChallenge.substring(0, 8)}...`,
      })

      window.location.href = authUrl.toString()
    } catch (error) {
      console.error('[Auth] Login initialization failed', error)
    }
  }, [])

  const logout = useCallback(() => {
    if (DEV_MODE) {
      setIsAuthenticated(false)
      setUser(null)
      return
    }

    setIsAuthenticated(false)
    setUser(null)
    setToken(null)
    clearRefreshToken()

    const logoutUrl = new URL(
      `${activeKeycloakConfig.url}/realms/${activeKeycloakConfig.realm}/protocol/openid-connect/logout`
    )
    logoutUrl.searchParams.set('client_id', activeKeycloakConfig.clientId)
    logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin)

    window.location.href = logoutUrl.toString()
  }, [activeKeycloakConfig])

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!user) {
        return false
      }
      return user.roles.includes(role) || user.roles.includes('admin')
    },
    [user]
  )

  const hasScope = useCallback(
    (scope: string): boolean => {
      if (DEV_MODE) {
        return true
      }
      return tokenHasScope(token, scope)
    },
    [token]
  )

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        token,
        login,
        logout,
        hasRole,
        hasScope,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
